// Purpose: Optimized image component with lazy loading and progressive enhancement
// Supports local optimized images and loading states for better performance
// Handles image path objects and string paths directly

import React, { useState, useRef } from 'react';
import './OptimizedImage.scss';

const OptimizedImage = ({
  src,
  alt,
  className = '',
  loading = 'lazy',
  priority = false,
  sizes = '100vw',
  onLoad,
  onError,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Generate optimized image paths from various input formats
  const getImagePaths = (src) => {
    if (!src) return null;

    // Handle image path objects ({ jpeg: "..." })
    if (typeof src === 'object' && src.jpeg) {
      return {
        jpeg: `/assets/images/${src.jpeg}`,
        thumbnail: src.thumbnail ? `/assets/images/${src.thumbnail}` : null
      };
    }

    // Handle string paths (from other collections)
    if (typeof src === 'string') {
      // Remove any existing /assets/images/ prefix to normalize
      const cleanSrc = src.replace(/^\/assets\/images\//, '');
      const pathParts = cleanSrc.split('/');
      const filename = pathParts[pathParts.length - 1];
      const basePath = pathParts.slice(0, -1).join('/');
      const nameWithoutExt = filename.split('.')[0];
      const ext = filename.includes('.') ? filename.split('.').pop().toLowerCase() : 'jpg';

      // Check if it's a hash-based filename (format: type-hash.ext)
      const hashMatch = filename.match(/^([^-]+)-([^.]+)\./);
      if (hashMatch) {
        const [, type, hash] = hashMatch;
        return {
          jpeg: `/assets/images/${basePath}/${type}-${hash}.jpg`,
          thumbnail: null // Don't assume -thumb.jpg exists for string paths (collection images)
        };
      }

      // For direct filename patterns (preserve extension for .png, .jpg, etc.)
      return {
        jpeg: `/assets/images/${basePath}/${nameWithoutExt}.${ext}`,
        thumbnail: null // Don't assume -thumb.jpg exists for string paths (collection images)
      };
    }

    console.warn('OptimizedImage: Unsupported src format:', src);
    return null;
  };

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  const imagePaths = getImagePaths(src);

  if (!imagePaths) {
    return (
      <div
        className={`optimized-image optimized-image--placeholder ${className}`}
        {...props}
      >
        <span className="optimized-image__fallback">
          {alt?.charAt(0) || '?'}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`optimized-image ${className} ${isLoaded ? 'optimized-image--loaded' : ''} ${hasError ? 'optimized-image--error' : ''}`}
      {...props}
    >
      {/* Thumbnail for progressive loading */}
      {imagePaths.thumbnail && !isLoaded && (
        <img
          src={imagePaths.thumbnail}
          alt=""
          className="optimized-image__thumbnail"
          aria-hidden="true"
        />
      )}

      {/* Main optimized image */}
      <img
        ref={imgRef}
        src={imagePaths.jpeg}
        alt={alt}
        loading={priority ? 'eager' : loading}
        className="optimized-image__img"
        onLoad={handleLoad}
        onError={handleError}
        sizes={sizes}
      />

      {/* Loading indicator */}
      {!isLoaded && !hasError && (
        <div className="optimized-image__loading">
          <div className="optimized-image__spinner"></div>
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="optimized-image__error">
          <span className="optimized-image__fallback">
            {alt?.charAt(0) || '?'}
          </span>
        </div>
      )}
    </div>
  );
};

// Utility function for other components that need image path resolution
export const getImageUrl = async (path) => {
  if (!path) return null;

  // If it's already a full URL, return as is
  if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) {
    return path;
  }

  // Normalize local image paths
  if (typeof path === 'string') {
    const cleanPath = path.replace(/^\/?(assets\/images\/)?/, '');
    return `/assets/images/${cleanPath}`;
  }

  return null;
};

export default OptimizedImage;